import type { ComponentType, FC, PropsWithChildren, ReactElement } from "react";
import { Children, cloneElement, isValidElement, useEffect, useState } from "react";

import type { IAjax } from "@Components/Hooks/useAjax";
import useAjax from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";

import type { IComponent } from "../Component";

import * as css from "./Repeater.module.css";

export * from "./Templates";

export type IRepeater = PropsWithChildren &
    IAjax<IComponent[]> & {
        noResultsTemplate?: React.ReactNode;
    };

export const RepeaterV2: FC<IRepeater> = (props) => {
    const { request, isLoading, errorContainer } = useAjax<IComponent[]>(props);
    const [items, setItems] = useState(props.initialData || []);

    useEffect(() => {
        if (!props.initialData) {
            request();
        }
    }, []);

    useEffect(() => {
        setItems(props.initialData || []);
    }, [props.initialData]);

    const getTemplate = () => {
        if (!props.children) {
            return null;
        }

        const templates: JSX.Element[] = [];

        Children.forEach(props.children, (child: ReactElement<ComponentType>) => {
            if (isValidElement(child)) {
                templates.push(child);
            }
        });

        if (templates.length === 1) {
            return templates[0];
        }
        if (templates.length > 1) {
            console.error(`Repeater can only have one element defined, but found ${templates.length}`);
        }
        return null;
    };

    const transformTemplate = () => {
        if (!items || isLoading) {
            return null;
        }

        const template = getTemplate();

        return items.map(
            (prop, i) =>
                template && cloneElement(template, { key: prop.id || prop.key || i, childProps: prop }),
        );
    };

    const getResults = () => {
        if (!props.children) {
            return null;
        }

        if (isLoading === false && items.length === 0) {
            return props.noResultsTemplate;
        }

        const res = transformTemplate();

        return res;
    };

    return (
        <div className={css.repeaterComponent}>
            {getResults()}
            {isLoading && <Loading isModal />}
            {errorContainer}
        </div>
    );
};

export default RepeaterV2;
